import { type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';

export interface PageSwitchTab {
  value: string;
  label: string;
  element: ReactNode;
  /** A tab the tenant has switched off is left out; the first visible tab is the default. */
  visible?: boolean;
}

interface PageSwitchProps {
  /** Query parameter that carries the active tab (`?tab=`). */
  param?: string;
  tabs: PageSwitchTab[];
  /** Test id for the tab strip. */
  testId?: string;
  /** Tab to open when the URL names none; the first visible tab otherwise. */
  defaultValue?: string;
}

/**
 * One sidebar item, several pages: a tab strip above a full page, keyed by
 * `?tab=`. Each page keeps its own heading, description and states, so a
 * page can move under a switch (or out again) without being rewritten. The
 * rest of the query string is preserved when tabs change, and a link that
 * names a tab the tenant has switched off falls back to the first visible one.
 */
export default function PageSwitch({ param = 'tab', tabs, testId = 'page-switch', defaultValue }: PageSwitchProps) {
  const [params, setParams] = useSearchParams();
  const visible = tabs.filter((t) => t.visible !== false);
  if (visible.length === 0) return null;
  const requested = params.get(param) ?? defaultValue;
  const active = visible.find((t) => t.value === requested) ?? visible[0];

  const select = (value: string) => {
    const next = new URLSearchParams(params);
    next.set(param, value);
    setParams(next);
  };

  return (
    <div className="space-y-6">
      {visible.length > 1 && (
        <Tabs value={active.value} onValueChange={select}>
          <TabsList data-testid={testId}>
            {visible.map((t) => <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
      )}
      <div key={active.value}>{active.element}</div>
    </div>
  );
}
